import { useNavigate } from "react-router-dom";

// ─── Route map ─────────────────────────────────────────────────────────────
// Keep in sync with server/service/alpha_chat_intent.go.
const ACTION_ROUTES: Record<string, string> = {
  // 主模块
  "AI 看盘": "/",
  "AI助手": "/ai-trader",

  // 研报
  "研报生成": "/research",        // + 拼接 /generate/:code
  "研报库": "/research/library",
  "研报详情": "/research/report/", // + 拼接 id

  // 策略
  "策略": "/ai-strategy",
  "建策略": "/ai-strategy/create",
  "编辑策略": "/ai-strategy/edit/", // + 拼接 id
  "策略详情": "/ai-strategy/detail/", // + 拼接 id

  // 持仓 & 自选
  "持仓": "/positions",
  "自选": "/watchlist",

  // 个股
  "个股详情": "/stock/",           // + 拼接 code

  // 个人
  "个人中心": "/profile",
  "我的统计": "/profile/stats",
  "订阅": "/profile/subscription",

  // 辅助
  "通知中心": "/notifications",
  "成就": "/achievements",
  "行情": "/",
  "新闻": "/news",
  "日历": "/calendar",
  "研究员": "/",
  "设置": "/profile",
};

// ─── Intent type ───────────────────────────────────────────────────────────

export interface ChatIntent {
  immediate: string;
  secondary: string[];
  params?: Record<string, string>;
  reason?: string;
}

// ─── Stock code/match helpers ──────────────────────────────────────────────

/** Extract first 6-digit stock code from text */
function findStockCode(text: string): string | null {
  const m = text.match(/\b(\d{6})\b/);
  return m ? m[1] : null;
}

/** Extract stock name near a code */
function findStockName(text: string, code: string): string | null {
  const m1 = text.match(new RegExp(`([\\u4e00-\\u9fa5]{2,8})\\(?${code}\\)?`));
  if (m1) return m1[1];
  const m2 = text.match(new RegExp(`${code}\\s*([\\u4e00-\\u9fa5]{2,8})`));
  if (m2) return m2[1];
  return null;
}

// ─── Legacy marker parsing ─────────────────────────────────────────────────

/** Parse `[去XXX]` markers from AI response text. */
export function parseActions(text: string): string[] {
  const actions: string[] = [];
  const re = /\[去([^\]]+)\]/g;
  let match;
  while ((match = re.exec(text)) !== null) {
    const action = match[1].split(":")[0].trim();
    if (ACTION_ROUTES[action]) {
      actions.push(action);
    }
  }
  return [...new Set(actions)];
}

/** Strip `[去XXX]` markers for display. */
export function stripActions(text: string): string {
  return text.replace(/\[去[^\]]+\]/g, "").trim();
}

// ─── Button action handler ─────────────────────────────────────────────────

function buildUrl(action: string, params?: Record<string, string>): string {
  const baseUrl = ACTION_ROUTES[action];
  if (!baseUrl) return "/";

  // Append dynamic params
  if (params?.code && action === "个股详情") return `/stock/${params.code}`;
  if (params?.code && action === "研报生成") {
    const name = params.name || "";
    return `/research/generate/${params.code}?step=quiz&name=${encodeURIComponent(name)}`;
  }
  if (params?.id && action === "研报详情") return `/research/report/${params.id}`;
  if (params?.id && action === "策略详情") return `/ai-strategy/detail/${params.id}`;
  if (params?.id && action === "编辑策略") return `/ai-strategy/edit/${params.id}`;

  // Fallback — if baseUrl ends with /, it expects a param but none given
  if (baseUrl.endsWith("/") && baseUrl.length > 1) return baseUrl;

  return baseUrl;
}

// ─── Props ─────────────────────────────────────────────────────────────────

interface ActionButtonsProps {
  text: string;
  context?: string;
  /** Structured intent from server SSE (overrides text-only parsing) */
  intent?: ChatIntent | null;
}

// ─── Component ─────────────────────────────────────────────────────────────

export default function ActionButtons({ text, context, intent }: ActionButtonsProps) {
  const navigate = useNavigate();

  // ── Resolve actions from dual sources ──────────────────────────────────
  const fromMarkers = parseActions(text);
  const fromIntent = intent?.immediate ? [intent.immediate, ...(intent.secondary || [])] : [];
  // Structured intent takes priority, markers are fallback
  const actionList = fromIntent.length > 0 ? fromIntent : fromMarkers;
  if (actionList.length === 0) return null;

  const params = intent?.params;

  // Dedup while preserving order, and filter out invalid actions
  const seen = new Set<string>();
  const actions: string[] = [];
  for (const a of actionList) {
    const key = a.trim();
    if (!seen.has(key) && ACTION_ROUTES[key]) {
      seen.add(key);
      actions.push(key);
    }
  }
  if (actions.length === 0) return null;

  // First action is the primary recommendation
  const primary = actions[0];
  const secondary = actions.slice(1, 4); // max 4 buttons total

  // If no structured params, try to extract from text (fallback)
  const fallbackParams = (() => {
    if (params) return undefined; // already have structured params
    const searchText = [text, context].filter(Boolean).join(" ");
    const code = findStockCode(searchText);
    if (!code) return undefined;
    return { code, name: findStockName(searchText, code) || "" };
  })();

  const handleClick = (action: string) => {
    const url = buildUrl(action, params || fallbackParams);
    navigate(url);
  };

  return (
    <div className="mt-2 space-y-1.5">
      {/* Reason text (from intent) */}
      {intent?.reason && (
        <p className="text-[9px] text-foreground-400 italic leading-tight">
          {intent.reason}
        </p>
      )}

      {/* Primary action — prominent */}
      <div className="flex flex-wrap gap-1.5">
        <button
          onClick={() => handleClick(primary)}
          className="px-3 py-1.5 rounded-full text-[11px] font-semibold bg-primary-500 text-white hover:bg-primary-600 cursor-pointer transition-colors shadow-sm"
        >
          <i className="ri-arrow-right-s-line text-xs align-middle" />
          去{primary}
        </button>

        {/* Secondary actions */}
        {secondary.map((action) => (
          <button
            key={action}
            onClick={() => handleClick(action)}
            className="px-2.5 py-1 rounded-full text-[10px] font-medium bg-background-100 text-foreground-600 border border-background-200 hover:bg-background-200 cursor-pointer transition-colors whitespace-nowrap"
          >
            {action}
          </button>
        ))}
      </div>
    </div>
  );
}
